import React, { useState, useEffect } from 'react';
import { Mail, Chrome, Apple, Eye, EyeOff, Zap, Target, Brain, CheckCircle, ArrowRight, Sparkles, Database, TrendingUp, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '../hooks/useAuth';
import LanguageSwitcher from './LanguageSwitcher';
import Logo from '../assests/favicon-logo.png';
import { supabase } from '../lib/supabase';
import AppleImg from '../assests/icons/devicon_apple.svg';
import GoogleImg from '../assests/icons/devicon_google.svg';
import Header from '../components/sub-components/Header/Header';
import Hero from '../components/sub-components/Hero/Hero';
import Features from '../components/sub-components/Features/FeaturesSection';
import HowItWorks from '../components/sub-components/HowItWorks/HowItWorksSection';
import Pricing from '../components/sub-components/Pricing/PricingSection';
import BackgroundElements from '../components/sub-components/UI/BackgroundElements';
import Testimonials from '../components/sub-components/Testimonials/TestimonialsSection';
import MobileAppSection from '../components/sub-components/MobileApp/MobileAppSection';
import FAQSection from '../components/sub-components/FAQ/FAQSection';
// Upload section CTA copied from marketing site (lines 35-66)

interface WelcomeScreenProps {
  onAuthComplete: (userInfo: {
    email: string;
    name: string;
    provider: string;
  }) => void;
}

const WelcomeScreen: React.FC<WelcomeScreenProps> = () => {
  const { signInWithEmail, signUpWithEmail, signInWithProvider, error: authError, setError : setAuthError } = useAuth();
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [authProvider, setAuthProvider] = useState<string>('');
  const [showAuthSection, setShowAuthSection] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [isForgotPassowrd, setIsForgotPassword] = useState(false)
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [messageType, setMessageType] = useState<'success' | 'error' | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    if (showAuthSection) {
      document.getElementById('authentication-section')?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [showAuthSection]);

  const handleEmailAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    
    setError(null);
    setMessage(null);
    setMessageType(null);
    
    if (isForgotPassowrd) {
      if (!email) {
        setError(t('errors.fill_all_fields'));
        return;
      }
    } else {
      if (!email || !password || (isSignUp && !name)) {
        setError(t('errors.fill_all_fields') || 'Please fill in all required fields.');
        return;
      }
      if (password.length < 6) {
        setError(t('auth.password_min_length') || 'Password must be at least 6 characters long.');
        return;
      }
    }
  
    setIsAuthenticating(true);
    setAuthProvider('email');
  
    try {
      let result;
      if (isSignUp) {
        result = await signUpWithEmail(email, password, name);
      } else if (isForgotPassowrd) {
        result = await handleForgotPassword(email);
      } else {
        result = await signInWithEmail(email, password);
      }
      
      if (result.error) {
        let errorMessage = result.error.message;
        
        setError(errorMessage);
        setMessage(null);
        setMessageType(null);
        
      } else if (isSignUp) {
        setMessage(t('errors.email_verification'));
        setMessageType('success');
        setError(null);
      } else if (isForgotPassowrd) {
        setMessage(t('form.reset_email_sent'));
        setMessageType('success');
        setError(null);
        setIsForgotPassword(false);
        setShowEmailForm(true);
      }
    } catch (err: any) {
      setError(t('errors.connection') || 'Connection error. Please try again.');
      setMessage(null);
      setMessageType(null);
      
    } finally {
      setIsAuthenticating(false);
    }
  };


  const handleSocialAuth = async (provider: 'google' | 'apple') => {
    setIsAuthenticating(true);
    setAuthProvider(provider);
    setError(null);
  
    try {
      const { error } = await signInWithProvider(provider);
      if (error) {
        setError(error.message);
      }
    } catch (err) {
      setError(t('errors.connection'));
      setIsAuthenticating(false);
    }
  };
  

  const features = [
    {
      icon: <Brain className="w-8 h-8" />,
      title: t('features.ai.title'),
      description: t('features.ai.desc'),
    },
    {
      icon: <Target className="w-8 h-8" />,
      title: t('features.match_score.title'),
      description: t('features.match_score.desc'),
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: t('features.recommendations.title'),
      description: t('features.recommendations.desc'),
    },
  ];

  const benefits = t('benefits', { returnObjects: true }) as string[];

  const stats = [
    {
      icon: <Database className="w-6 h-6" />,
      number: t('stats.cv_analyzed.number') || '1,500+',
      label: t('stats.cv_analyzed.label') || 'CVs successfully analyzed',
      description:
        t('stats.cv_analyzed.description') ||
        'Database of CVs that found employment',
    },
    {
      icon: <TrendingUp className="w-6 h-6" />,
      number: t('stats.success_rate.number'),
      label: t('stats.success_rate.label'),
      description: t('stats.success_rate.description'),
    },
    {
      icon: <Target className="w-6 h-6" />,
      number: t('stats.ai_accuracy.number'),
      label: t('stats.ai_accuracy.label'),
      description: t('stats.ai_accuracy.description'),
    },
  ];

  const handleForgotPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    return { error };
  };

  const handleFlowForgotPassword = () => {
    setIsForgotPassword(true)
    clearErrors()
  }

  const clearErrors = () => {
    if (error) {
      setError(null);
    }
    if(authError){
      setAuthError(null)
    }
    if (message) {
      setMessage(null);
      setMessageType(null);
    }
  }

  return (
    <div className="min-h-screen bg-slate-900 text-slate-200 relative overflow-hidden">
      <BackgroundElements />
      <Header />
      <Hero />
      <Features />
      <HowItWorks />
      <Pricing />
      <Testimonials />
      {/* Language Switcher at top right */}
      <div className="absolute top-24 right-0 z-20">
        <LanguageSwitcher />
      </div>
      

      <div className="relative z-10 flex flex-col min-h-screen mt-28" id="authentication-section">
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="max-w-7xl w-full">
            <div className="flex flex-col items-center">
              <div className="text-center mb-4">
                <h2 className="text-5xl sm:text-6xl font-bold text-white mb-8">
                  {t('upload.sectionTitlePart1')}{' '}
                  <span className="bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent">
                    {t('upload.sectionTitleHighlight')}
                  </span>{' '}
                  {t('upload.sectionTitlePart2')}
                </h2>
              </div>
              {showAuthSection 
              ? <div className="animate-fadeInRight mb-28">
                  <div className="bg-slate-800 rounded-2xl shadow-2xl p-12 max-w-md mx-auto">
                    <div>
                      {!showEmailForm ? (
                        <div key="social-auth">
                          <div className="text-center mb-14">
                            <h2 className="text-2xl font-bold text-slate-200 mb-2">
                              {t('start_now')}
                            </h2>
                            <p className="text-slate-400">
                              {t('login_to_access')}
                            </p>
                          </div>

                          {(error || authError) && (
                            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start shadow-sm">
                              <AlertCircle className="w-5 h-5 text-red-500 mr-3 mt-0.5 flex-shrink-0" />
                              <span className="text-red-700 text-sm leading-relaxed">
                                {error || authError}
                              </span>
                            </div>
                          )}
                          {message && messageType === 'success' && (
                            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center">
                              <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                              <span className="text-green-700 text-sm">
                                {message}
                              </span>
                            </div>
                          )}

                          <div className="space-y-8">
                            <button
                              onClick={() => handleSocialAuth('google')}
                              disabled={isAuthenticating}
                              className="w-full flex items-center justify-center px-4 py-3 border border-gray-300 rounded-lg shadow-sm bg-white text-gray-700 hover:bg-gray-50 transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {isAuthenticating && authProvider === 'google' ? (
                                <div className="w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin mr-3"></div>
                              ) : (
                                <img src={GoogleImg} alt="Google" className="w-5 h-5 mr-3" />
                              )}
                              {t('continue_with_google')}
                            </button>

                            {/* Apple button */}
                            <button
                              onClick={() => handleSocialAuth('apple')}
                              disabled={isAuthenticating}
                              className="w-full flex items-center justify-center px-4 py-3 border border-gray-300 rounded-lg shadow-sm bg-black text-white hover:bg-gray-900 transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {isAuthenticating && authProvider === 'apple' ? (
                                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-3"></div>
                              ) : (
                                <img src={AppleImg} alt="Apple" className="w-5 h-5 mr-3" />
                              )}
                              {t('continue_with_apple')}
                            </button>

                            <div className="relative my-6">
                              <div className="absolute inset-0 flex items-center">
                                <div className="w-full border-t border-gray-300"></div>
                              </div>
                              <div className="relative flex justify-center text-sm">
                                <span className="px-2 bg-white text-gray-500">
                                  {t('or')}
                                </span>
                              </div>
                            </div>

                            {/* Email button */}
                            <button
                              onClick={() => setShowEmailForm(true)}
                              className="w-full flex items-center justify-center px-4 py-3 border border-indigo-300 rounded-lg shadow-sm bg-indigo-50 text-indigo-700 hover:bg-indigo-100 transition-all transform hover:scale-105"
                            >
                              <Mail className="w-5 h-5 mr-3" />
                              {t('continue_with_email')}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div key="email-auth">
                          <div className="text-center mb-6">
                            <h2 className="text-2xl font-bold text-slate-200 mb-2">
                              {isSignUp
                                ? t('form.signup_title') : isForgotPassowrd ? t('form.forgot_password')
                                : t('form.signin_title')}
                            </h2>
                            <p className="text-slate-300">
                              {isSignUp
                                ? t('form.signup_desc') : isForgotPassowrd ? t('form.forgot_desc')
                                : t('form.signin_desc')}
                            </p>
                          </div>
                          {(error || authError) && (
                            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start shadow-sm">
                              <AlertCircle className="w-5 h-5 text-red-500 mr-3 mt-0.5 flex-shrink-0" />
                              <span className="text-red-700 text-sm leading-relaxed">
                                {error || authError}
                              </span>
                            </div>
                          )}
                          {message && messageType === 'success' && (
                            <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start shadow-sm">
                              <CheckCircle className="w-5 h-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                              <span className="text-green-700 text-sm leading-relaxed">
                                {message}
                              </span>
                            </div>
                          )}

                          <form onSubmit={handleEmailAuth} className="space-y-4">
                            {isSignUp && (
                              <div>
                                <label className="block text-sm font-medium text-gray-400 mb-2">
                                  {t('form.full_name')}
                                </label>
                                <input
                                  type="text"
                                  value={name}
                                  onChange={(e) => setName(e.target.value)}
                                  className="w-full px-3 py-2 border text-slate-700 border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                  placeholder={t('form.full_name_placeholder')}
                                  required
                                />
                              </div>
                            )}

                            <div>
                              <label className="block text-sm font-medium text-gray-400 mb-2">
                                {t('form.email')}
                              </label>
                              <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="w-full px-3 py-2 border text-slate-700 border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                placeholder={t('form.email_placeholder')}
                                required
                              />
                            </div>

                            {!isForgotPassowrd && <div className={`${isSignUp ? '' : 'flex flex-col'} `}>
                              <label className="block text-sm font-medium text-gray-400 mb-2">
                                {t('form.password')}
                              </label>
                              <div className="relative">
                                <input
                                  type={showPassword ? "text" : "password"}
                                  value={password}
                                  onChange={(e) => setPassword(e.target.value)}
                                  className="w-full px-3 py-2 border text-slate-700 border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                  placeholder={t('form.password_placeholder')}
                                  required
                                  minLength={8}
                                />
                                <button
                                  type="button"
                                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                  onClick={() => setShowPassword(!showPassword)}
                                  aria-label={showPassword ? t("auth.hide_password") : t("auth.show_password")}
                                >
                                  {showPassword ? (
                                    <EyeOff className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                                  ) : (
                                    <Eye className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                                  )}
                                </button>
                              </div>
                            {(!isForgotPassowrd && !isSignUp) && <button type='button' className='text-indigo-600 place-self-end mt-1' onClick={handleFlowForgotPassword}>
                              {t('form.forgot_password')}
                            </button>}
                            </div>}
                            <button
                              type="submit"
                              disabled={
                                isAuthenticating ||
                                !email ||
                                (!isForgotPassowrd && (!password || (isSignUp && !name)))
                              }
                              className="w-full flex items-center justify-center px-4 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:shadow-lg transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {isAuthenticating ? (
                                <>
                                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-3"></div>
                                  {isSignUp
                                    ? t('form.creating') 
                                    : t('form.logging')}
                                </>
                              ) : (
                                <>
                                  {isSignUp
                                    ? t('form.create_account') : isForgotPassowrd ? t('form.forgot_password')
                                    : t('form.login')}
                                  {!isForgotPassowrd && <ArrowRight className="w-5 h-5 ml-2" />}
                                </> 
                              )}
                            </button>

                            <div className="text-center">
                              <button
                                type="button"
                                onClick={() => {
                                  setIsSignUp(!isSignUp);
                                  clearErrors()
                                }}
                                className="text-sm text-indigo-600 hover:text-indigo-800 transition-colors"
                              >
                                {isSignUp
                                  ? t('already_have_account')
                                  : t('no_account')}
                              </button>
                            </div>

                            {isForgotPassowrd && <div className="text-center">
                              <button
                                type="button"
                                onClick={() => {
                                  setIsForgotPassword(false)
                                }}
                                className="text-sm text-indigo-600 hover:text-indigo-800 transition-colors"
                              >
                                Back to login
                              </button>
                            </div>}

                            <button
                              type="button"
                              onClick={() => {
                                clearErrors()
                                setShowEmailForm(false);
                              }}
                              className="w-full text-sm text-gray-500 hover:text-gray-700 transition-colors"
                            >
                              {t('back_to_options')}
                            </button>
                          </form>
                        </div>
                      )}

                      <div className="mt-10 text-center">
                        <p className="text-xs text-gray-400">
                          {t('terms_text')}{' '}
                          <a
                            href="/terms-and-conditions"
                            className="text-indigo-600 hover:text-indigo-800 underline"
                          >
                            {t('terms')}
                          </a>{' '}
                          {t('and')}{' '}
                          <a
                            href="/privacy-policy"
                            className="text-indigo-600 hover:text-indigo-800 underline"
                          >
                            {t('privacy')}
                          </a>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              : <section className="py-20 bg-slate-900/30" id="upload-section">
                  <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">          
                    <div className="max-w-2xl mx-auto">
                      <div
                        onClick={() => setShowAuthSection(true)}
                        className="bg-slate-800/50 backdrop-blur-xl border-2 border-dashed border-slate-700/50 rounded-3xl p-16 text-center cursor-pointer hover:border-indigo-500/50 hover:bg-indigo-500/5 transition-all duration-300 group"
                      >
                        <div className="w-24 h-24 mx-auto mb-8 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-3xl flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform duration-300">
                          <Target className="w-12 h-12 text-white" />
                        </div>
          
                        <h3 className="text-3xl font-bold text-white mb-4">
                          {t('upload.ctaTitle')}
                        </h3>
                        <p className="text-slate-400 mb-8 text-lg">
                          {t('upload.ctaDescription')}
                        </p>
          
                        <div className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-2xl group-hover:from-indigo-700 group-hover:to-purple-700 transition-all duration-200 shadow-lg">
                          {t('upload.buttonLabel')}
                        </div>
                      </div>
                    </div>
                  </div>
                </section>
              }
            </div>
          </div>
        </div>
        <MobileAppSection />
        <FAQSection />
        {/* Footer */}
        {/* <footer className="text-center py-8 text-gray-300">
          <p className="text-sm">{t('footer.copyright')}</p>
          <p className="text-xs mt-2 opacity-75">
            {t('footer.subtitle')}
            <div className="mt-3 flex flex-wrap justify-center items-center gap-2 text-xs">
              <a
                href="/terms-and-conditions"
                className="hover:text-white underline"
              >
                {t('footer.terms_of_use')}
              </a>
              <span className="opacity-50">•</span>
              <a href="/privacy-policy" className="hover:text-white underline">
                {t('footer.privacy_policy')}
              </a>
              <span className="opacity-50">•</span>
              <a href="/legal-notice" className="hover:text-white underline">
                {t('footer.legal_notices')}
              </a>
            </div>
          </p>
        </footer> */}
      </div>
    </div>
  );
};

export default WelcomeScreen;
